/*
  Where each node of the family navigator goes. The tree flows from left to right, one column for each level,
  so the names can be read along the line. Leaves take the next free row; a node sits in the middle of its
  children. The result goes to Cytoscape's built-in "preset" layout, so no layout code is added to the library
  and the same input always gives the same picture.
*/

import type { Id } from "../../../api/types";
import type { NavModel } from "../model/nav";

export interface Point {
  x: number;
  y: number;
}

export interface TreeLayout {
  positions: Map<Id, Point>;
  /** The nodes drawn, in tree order. */
  order: Id[];
  width: number;
  height: number;
}

export const COLUMN_WIDTH = 200;
export const ROW_HEIGHT = 44;

/** Lay out the nodes that are not hidden by a folded parent. */
export function layoutForest(model: NavModel, expanded: ReadonlySet<Id>, spacing: { column?: number; row?: number } = {}): TreeLayout {
  const column = spacing.column ?? COLUMN_WIDTH;
  const row = spacing.row ?? ROW_HEIGHT;
  const positions = new Map<Id, Point>();
  const order: Id[] = [];
  let nextRow = 0;
  let deepest = 0;

  const place = (id: Id, depth: number): number => {
    order.push(id);
    deepest = Math.max(deepest, depth);
    const children = expanded.has(id) ? (model.childrenOf.get(id) ?? []) : [];
    let y: number;
    if (children.length === 0) {
      y = nextRow * row;
      nextRow += 1;
    } else {
      const ys = children.map((child) => place(child.id, depth + 1));
      y = ((ys[0] as number) + (ys[ys.length - 1] as number)) / 2;
    }
    positions.set(id, { x: depth * column, y });
    return y;
  };
  for (const root of model.roots) place(root.id, 0);

  return { positions, order, width: (deepest + 1) * column, height: Math.max(1, nextRow) * row };
}
